/**
 * CommentThread — the reply thread that lives under a single notification in the
 * notification center, making the bell lightly two-way.
 *
 * `canSee` is passed by the notification center (it already knows which rows the
 * caller may see). It is a courtesy that keeps a child from ever loading a thread on
 * an adults-only notification; worker/routes/comments.ts enforces the real boundary
 * in SQL — this component is never a security control.
 *
 * A failed reply never loses what was typed.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { Comment } from '../../shared/types';
import { parseTs, relativeTime } from './util';

const MAX_BODY = 2000;

export function CommentThread({
  notificationId,
  canSee,
}: {
  notificationId: number;
  canSee: boolean;
}) {
  const identity = useIdentity();
  const isAdult = identity.role === 'adult';

  const [items, setItems] = useState<Comment[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const rows = await api.get<Comment[]>(
        `/comments?target_type=notification&target_id=${notificationId}`
      );
      setItems(rows);
    } catch (err) {
      setLoadError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't load replies. Try again in a moment."
      );
    }
  }, [notificationId]);

  useEffect(() => {
    if (canSee) void load();
  }, [canSee, load]);

  // Not the caller's to see — render nothing. The API would refuse anyway.
  if (!canSee) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setPostError(null);
    const body = text.trim();
    if (!body) {
      setPostError('Write something first.');
      return;
    }

    setPosting(true);
    try {
      const created = await api.post<Comment>('/comments', {
        target_type: 'notification',
        target_id: notificationId,
        body,
      });
      setItems((prev) => [...(prev ?? []), created]);
      setText(''); // only clear on a confirmed save
    } catch (err) {
      // Keep the text exactly as typed.
      setPostError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't post that reply. Try again in a moment."
      );
    } finally {
      setPosting(false);
    }
  }

  async function remove(c: Comment) {
    setActionError(null);
    setBusyId(c.id);
    try {
      await api.del(`/comments/${c.id}`);
      setItems((prev) => (prev ?? []).filter((x) => x.id !== c.id));
    } catch (err) {
      setActionError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't delete that reply. Try again in a moment."
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mt-3 border-t border-border pt-3">
      {items === null && !loadError && (
        <p className="text-xs text-ink-faint">Loading replies…</p>
      )}

      {loadError && (
        <div className="flex items-center gap-2 text-xs text-clay">
          <span role="alert">{loadError}</span>
          <button
            type="button"
            onClick={() => {
              setItems(null);
              void load();
            }}
            className="font-medium underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
          >
            Retry
          </button>
        </div>
      )}

      {items !== null && !loadError && (
        <>
          {items.length === 0 ? (
            <p className="text-xs text-ink-faint">No replies yet.</p>
          ) : (
            <ul className="space-y-3">
              {items.map((c) => {
                const at = parseTs(c.created_at);
                const mine = c.member_id === identity.memberId;
                const name = c.author?.name ?? 'Someone';
                const color = c.author?.color ?? 'var(--color-ink-soft)';
                return (
                  <li key={c.id} className="flex items-start gap-2.5">
                    <span
                      aria-hidden="true"
                      className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-semibold" style={{ color }}>
                          {name}
                        </span>
                        <time dateTime={at.toISOString()} className="text-xs text-ink-faint">
                          {relativeTime(at)}
                        </time>
                      </p>
                      <p className="text-sm whitespace-pre-wrap text-ink-soft">{c.body}</p>
                    </div>
                    {(mine || isAdult) && (
                      <button
                        type="button"
                        onClick={() => remove(c)}
                        disabled={busyId === c.id}
                        aria-label={`Delete ${name}'s reply`}
                        className="inline-flex min-h-11 shrink-0 items-center rounded px-2 text-xs text-ink-faint transition-colors hover:text-clay focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk disabled:opacity-50"
                      >
                        Delete
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {actionError && (
            <p role="alert" className="mt-2 text-xs text-clay">
              {actionError}
            </p>
          )}

          <form onSubmit={submit} className="mt-3">
            <label htmlFor={`reply-${notificationId}`} className="sr-only">
              Write a reply
            </label>
            <textarea
              id={`reply-${notificationId}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Reply…"
              rows={2}
              maxLength={MAX_BODY}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk"
            />
            {postError && (
              <p role="alert" className="mt-1 text-xs text-clay">
                {postError}
              </p>
            )}
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={posting || !text.trim()}
                className="inline-flex min-h-11 items-center rounded-full bg-dusk px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusk disabled:opacity-50"
              >
                {posting ? 'Posting…' : 'Reply'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
